import snakeCase from 'lodash/snakeCase';

import { CommunityPrivacy } from './types';

export enum COMMUNITIES {
    /**
     * @value Back to community
     * @description Button used to redirect the user to the current community default page
     */
    BACK_TO_COMMUNITY = 'COMMUNITY.STATIC_PAGES_BACK_BUTTON',
    /**
     * @value Community file library cannot be deactivated after creation
     * @description Tooltip is shown when a commutnity is edited
     */
    CANNOT_CHANGE_RESTRICTED_COMMUNITY_STORAGE_ACTIVATION = 'ADMIN.COMMUNITY.SECURE_STORAGE.TOOLTIP_EDITION_MODE',
    /**
     * @value Community administrators
     */
    COMMUNITY_MANAGERS = 'COMMUNITY.COMMUNITY_ADMINISTRATORS',
    /**
     * @value Community name
     */
    COMMUNITY_NAME = 'COMMUNITIES.COMMUNITY_NAME',
    /**
     * @value Community selection
     * @description Text used on widget settings to pick specific community from a list
     */
    COMMUNITY_SELECTION = 'COMMUNITY.COMMUNITY_SELECTION',
    /**
     * @value Engage members to collaborate with community services.
     * @description Description of the "Lumapps services" section of the "Services" tab of the community configuration dialog.
     */
    CONFIGURATION_DIALOG_SERVICES_LUMAPPS_DESCRIPTION = 'FRONT.COMMUNITY.CONFIGURATION_DIALOG.SERVICES.LUMAPPS.DESCRIPTION',
    /**
     * @value LumApps services
     * @description Title of the "Lumapps services" section of the "Services" tab of the community configuration dialog
     */
    CONFIGURATION_DIALOG_SERVICES_LUMAPPS_TITLE = 'FRONT.COMMUNITY.CONFIGURATION_DIALOG.SERVICES.LUMAPPS.TITLE',
    /**
     * @value Create another
     * @description Action to create another community. For gendered languages, the object is a community, please decline it accordingly
     *
     * Note: can't be global key because it's in the context of a designate a community, so in gendered languages we can assume the name gender ("Créer UNE autre" and not "Créer UN autre")
     */
    CREATE_ANOTHER = 'COMMUNITIES.CREATE_ANOTHER',
    /**
     * @value Group members are synced and the services they can access become available to the community.
     * @description Description of the option to Create a community linked to an existing Office 365 group
     */
    CREATE_MS_GROUPS_EXISTING_OFFICE_365_DESCRIPTION = 'COMMUNITIES.CREATE_MS_GROUPS_EXISTING_OFFICE_365_DESCRIPTION',
    /**
     * @value Create community with an existing Office 365 group
     * @description Title of the option to Create a community linked to an existing Office 365 group
     */
    CREATE_MS_GROUPS_EXISTING_OFFICE_365_TITLE = 'COMMUNITIES.CREATE_MS_GROUPS_EXISTING_OFFICE_365_TITLE',
    /**
     * @value Create a new group synced with Office 365 integrations.
     * @description Description of the option to Create a community linked to a new Office 365 group
     */
    CREATE_MS_GROUPS_NEW_OFFICE_365_DESCRIPTION = 'COMMUNITIES.CREATE_MS_GROUPS_NEW_OFFICE_365_DESCRIPTION',
    /**
     * @value Create a community with a new Office 365 group
     * @description Title of the option to Create a community linked to a new Office 365 group
     */
    CREATE_MS_GROUPS_NEW_OFFICE_365_TITLE = 'COMMUNITIES.CREATE_MS_GROUPS_NEW_OFFICE_365_TITLE',
    /**
     * @value Members are added by the community administrators. Office 365 integrations will not be accessible.
     */
    CREATE_MS_GROUPS_WITHOUT_OFFICE_365_DESCRIPTION = 'COMMUNITIES.CREATE_MS_GROUPS_WITHOUT_OFFICE_365_HELPER',
    /**
     * @value Create a community without an Office 365 Group
     * @description Title of the option to Create a community NOT linked to an Office 365 group
     */
    CREATE_MS_GROUPS_WITHOUT_OFFICE_365_TITLE = 'COMMUNITIES.CREATE_MS_GROUPS_WITHOUT_OFFICE_365_TITLE',
    /**
     * @value Current community
     * @description Text used on widget selection to get only elements from current community
     */
    CURRENT_COMMUNITY = 'COMMUNITY.CURRENT_COMMUNITY',
    /**
     * @value Homepage
     */
    DASHBOARD = 'FRONT.COMMUNITY.DASHBOARD',
    /**
     * @value Edit navigation
     */
    EDIT_NAVIGATION = 'COMMUNITIES.EDIT_NAVIGATION',
    /**
     * @value File library
     * @description Label of the community navigation item which redirect use to the community file library
     */
    FILE_LIBRARY = 'COMMUNITIES.FILE_LIBRARY',
    /**
     * @value Added groups cannot be removed. Users can still decide to unfollow the community.
     * @description Information text explaining that groups that has been or will be added to the "automatic follow" feature of a community, won't be able to be removed later, but that the users can individually unfollow said communities if they want to.
     */
    FORCE_FOLLOW_ADDED_GROUPS_CANNOT_BE_REMOVED = 'COMMUNITIES.FORCE_FOLLOW_ADDED_GROUPS_CANNOT_BE_REMOVED',
    /**
     * @value {"one":"%COUNT% group following","other":"%COUNT% groups following"}
     * @description Number of groups that are currently following a community. If "following" adjective is not possible, translate with verb as in "1 group follows / n groups follow"
     */
    GROUPS_FOLLOWING_COUNT = 'COMMUNITIES.GROUPS_FOLLOWING_COUNT',
    /**
     * @value Homepage
     */
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    HOMEPAGE = 'FRONT.COMMUNITY.DASHBOARD',
    /**
     * @value Edit layout
     */
    LAYOUT = 'SETTINGS_MENU_COMMUNITY_LAYOUT',
    /**
     * @value Drive
     */
    MEDIA = 'FRONT.COMMUNITY.DRIVE_VIEW',
    /**
     * @value {"one":"member","other":"members"}
     */
    MEMBER = 'FRONT.COMMUNITY.MEMBER',
    /**
     * @value The changes you made will be discarded.
     */
    MIGHT_LOSE_CHANGES = 'FRONT.COMMUNITY.MIGHT_LOSE_CHANGES',
    /**
     * @value Upgrade to Space
     * @description WARNING: the word "Space" should be kept as it is since it's a feature name.
     *
     * This is a label for a button.
     */
    MIGRATE_TO_SPACE = 'COMMUNITY.MIGRATE_TO_SPACE',
    /**
     * @value Communities
     */
    NAV = 'ADMIN.NAV.COMMUNITIES',
    /**
     * @value Edit navigation
     */
    NAVIGATION_EDIT = 'COMMUNITIES.NAVIGATION_EDIT',
    /**
     * @value Create communities to give your users a voice.<br/>Allow them to share, collaborate and create ideas.
     * @description Message appearing when there are no communities in the admin communities page. It invites users to create a first community.
     */
    NO_COMMUNITIES_DESCRIPTION = 'ADMIN.COMMUNITIES.NO_COMMUNITIES_DESCRIPTION',
    /**
     * @value Office 365 group
     * @description Label to display office 365 groups.
     */
    OFFICE_365_GROUP = 'COMMUNITIES.OFFICE_365_GROUP',
    /**
     * @value Categories
     */
    POST_CATEGORIES = 'ADMIN.COMMUNITY.SETUP.FIELD.CATEGORIES',
    /**
     * @value Post types
     * @description Title of the post types selection in the "Posts" tab of the community configuration dialog.
     * Titlte of the section that lets the user configure the type of posts that can be used in a community
     */
    POST_TYPES = 'FRONT.COMMUNITY.CONFIGURATION_DIALOG.POSTS.TITLE',
    /**
     * @value Default
     */
    POST_TYPE_DEFAULT = 'COMMUNITY_POST_TYPE_DEFAULT',
    /**
     * @value Event
     */
    POST_TYPE_EVENT = 'COMMUNITY_POST_TYPE_EVENT',
    /**
     * @value Idea
     */
    POST_TYPE_IDEA = 'COMMUNITY_POST_TYPE_IDEA',
    /**
     * @value Question
     */
    POST_TYPE_QUESTION = 'COMMUNITY_POST_TYPE_QUESTION',
    /**
     * @value Preview as Space
     * @description "Space" should not be translated, it's a product name.
     */
    PREVIEW_AS_SPACE = 'COMMUNITY.PREVIEW_AS_SPACE',
    /**
     * @value Preview the current Community as a Space
     * @description "Space" should not be translated, it's a feature name.
     */
    PREVIEW_AS_SPACE_DESCRIPTION = 'COMMUNITY.PREVIEW_AS_SPACE_DESCRIPTION',
    /**
     * @value This preview of %COMMUNITY_NAME% displays how it will look as a Space. Navigation is possible but interactivity may be altered.
     */
    PREVIEW_AS_SPACE_WARNING = 'COMMUNITY.PREVIEW_AS_SPACE_WARNING',
    /**
     * @value Private
     */
    PRIVACY_DESCRIPTION_ONLY = 'COMMUNITY.PRIVACY_DESCRIPTION_ONLY',
    /**
     * @value All users can access the community and ask to join. Only members can view and create posts. Community administrators process membership requests.
     */
    PRIVACY_DESCRIPTION_ONLY_DESCRIPTION = 'COMMUNITY.PRIVACY_PRIVATE_DESCRIPTION',
    /**
     * @value Open
     */
    PRIVACY_OPEN = 'COMMUNITY.PRIVACY_OPEN',
    /**
     * @value All users can access the community, view its posts and contribute.
     * @description Description of the visibility "open". The user sees that description when selecting the visibility for a community they are creating.
     */
    PRIVACY_OPEN_DESCRIPTION = 'COMMUNITY.PRIVACY_OPEN.DESCRIPTION',
    /**
     * @value Members only contribution
     */
    PRIVACY_READ_ONLY = 'COMMUNITY.PRIVACY_READ_ONLY',
    /**
     * @value All users can search for the community, view its posts and ask to join it. Only members can contribute. Community administrators process membership requests.
     */
    PRIVACY_READ_ONLY_DESCRIPTION = 'COMMUNITY.PRIVACY_MEMBERS_ONLY_CONTRIBUTION_DESCRIPTION',
    /**
     * @value Secret
     */
    PRIVACY_RESTRICTED = 'COMMUNITY.PRIVACY_RESTRICTED',
    /**
     * @value Only members can see, access and post in the community. Members must be added by community administrators.
     */
    PRIVACY_RESTRICTED_DESCRIPTION = 'COMMUNITY.PRIVACY_SECRET_DESCRIPTION',
    /**
     * @value Community saved successfully
     */
    SAVED = 'ADMIN.COMMUNITIES.SAVED',
    /**
     * @value There are no communities.
     * @description Appears instead of a list of results during a community search when there are no results despite the fact that the user looked for any community.
     * The user didn't write anything in the search field so he is looking for any existing community but there are simply no communities at all.
     */
    SEARCH_NO_RESULTS = 'FRONT.COMMUNITY.SEARCH.NO_RESULTS',
    /**
     * Usage: Message to use when displaying other services
     *
     * Translation: Other services
     * @value You are not following any communities.
     * @description Appears instead of a list of results during a community search when there are no results despite the fact that the user looked for any followed community.
     * The user didn't write anything in the search field so he is looking for any followed community but there are none.
     */
    SEARCH_NO_RESULTS_WITH_FILTER = 'FRONT.COMMUNITY.SEARCH.NO_RESULTS_WITH_FILTER',
    /**
     * @value All communities
     * @description Choice of search perimeter when searching for a community to share a post to.
     * This value is used to share in All Communities
     */
    SEARCH_SCOPE_ALL = 'FRONT.COMMUNITY.SEARCH.SCOPE.ALL',
    /**
     * @value Followed communities
     * @description Choice of search perimeter when searching for a community to share a post to.
     * This value is used to search only in the communities the user is following
     */
    SEARCH_SCOPE_FOLLOWING = 'FRONT.COMMUNITY.SEARCH.SCOPE.FOLLOWING',
    /**
     * @value See all communities
     * @description In the community picker if the filter "followed communities" is active and there are no results the user is invited to deactivate the filter thanks to a button with this label.
     */
    SEARCH_SEE_ALL_COMMUNITIES = 'FRONT.COMMUNITY.SEARCH.SEE_ALL_COMMUNITIES',
    /**
     * @value {"one":"%COUNT% community selected","other":"%COUNT% communities selected"}
     */
    SELECTED_COMMUNITIES_COUNT = 'COMMUNITIES.SELECTED_COMMUNITIES_COUNT',
    /**
     * @value No communities yet
     */
    SELECTED_LIST_EMPTY_STATE_TITLE = 'COMMUNITIES.SELECTED_LIST_EMPTY_STATE_TITLE',
    /**
     * @value Select communities
     * @description Text used to pick specific communities in widget settings
     */
    SELECT_COMMUNITIES = 'COMMUNITY.SELECT_COMMUNITIES',
    /**
     * @value Select a community
     */
    SELECT_COMMUNITY = 'COMMUNITY_POST_SELECT_PARENT_CONTENT',
    /**
     * @value Select from Drive
     */
    SELECT_FROM_DRIVE = 'ADMIN.COMMUNITY.SELECT_FROM_DRIVE',
    /**
     * @value Select a group
     */
    SELECT_OFFICE_GROUP = 'ADMIN.COMMUNITY.CREATION.PICK.GROUP.LABEL',
    /**
     * @value URL
     */
    SETUP_FIELD_COMMUNITY_URL = 'ADMIN.COMMUNITY.SETUP.FIELD.COMMUNITY_URL',
    /**
     * @value Create a homepage for the community.
     */
    SETUP_FIELD_DESCRIPTION_HOMEPAGE = 'ADMIN.COMMUNITY.SETUP.FIELD.DESCRIPTION.HOMEPAGE',
    /**
     * @value An error occurred while saving community
     */
    SETUP_SAVE_ERROR = 'ADMIN.COMMUNITY.SETUP.SAVE.ERROR',
    /**
     * @value Visibility
     */
    STEP_VISIBILITY = 'ADMIN.COMMUNITY.SETUP.STEP.VISIBILITY',
    /**
     * @value There are no communities
     */
    THERE_ARE_NO_COMMUNITIES = 'COMMUNITIES.THERE_ARE_NO_COMMUNITIES',
    /**
     * @value Community visibility cannot be changed when community file library is activated
     * @description Label displayed on Visibility options during the edition of a community
     */
    VISIBILITY_CANNOT_BE_CHANGED_WITH_RESTRICTED_STORAGE = 'ADMIN.COMMUNITY.SECURE_STORAGE.VISIBILITY_OPTIONS_LABEL_DURING_EDITION',
}

enum SPACES_SPECIFIC_KEYS {
    /**
     * @value Administrators
     */
    COMMUNITY_MANAGERS = 'ADMIN.NAV.INSTANCE_ADMINS',
    /**
     * @value A minimum of one service must be activated.
     */
    CONFIGURATION_SERVICE_REQUIRED = 'SPACES.CONFIGURATION_SERVICE_REQUIRED',
    /**
     * @value Create a publication to start engaging with members.
     * @description Message displayed in the space feed empty state to encourage users to create a publication.
     *
     */
    CREATE_PUBLICATION_TO_ENGAGE = 'SPACES.CREATE_PUBLICATION_TO_ENGAGE',
    /**
     * @value Edit page
     * @description Key to be used in the Space settings modal, to edit the space homepage.
     */
    EDIT_PAGE = 'SPACES.EDIT_PAGE',
    /**
     * @value Feed
     * @description This is a part of the platform where users can see an aggregated stream of all their subscriptions. Take inspiration from Facebook on whether you should translate it or keep the english word
     */
    FEED = 'GLOBAL.FEED',
    /**
     * @value Community feed is empty
     * @description Message displayed when a space feed has no publication yet.
     *
     */
    FEED_IS_EMPTY = 'SPACES.FEED_IS_EMPTY',
    /**
     * @value Space header is not editable
     * @description The term "Space" must not be translated, it is a male name.
     */
    HEADER_NOT_EDITABLE = 'SPACES.HEADER_NOT_EDITABLE',
    /**
     * @value Learning
     * @description Type of training
     */
    LEARNING = 'GLOBAL.LEARNING',
    /**
     * @value Documents
     * @description Name of the Space service used to store documents
     */
    MANAGED_MEDIA_LIBRARY = 'SPACES.MANAGED_MEDIA_LIBRARY',
    /**
     * @value Microsoft 365 group
     */
    MICROSOFT_365_GROUP = 'SPACES.MICROSOFT_365_GROUP',
    /**
     * @value Spaces
     * @description Default name for the feature known as "Spaces" (a new version of the communities)
     * The term "Space" must not be translated, it is a male name.
     *
     */
    NAV = 'GLOBAL.FEATURE_SPACES',
    /**
     * @value New publications will be listed here.
     * @description Message displayed on the space feed empty state if user doesn't have the sufficient permissions to create a publication;
     */
    NEW_PUBLICATIONS_LISTED_HERE = 'SPACES.NEW_PUBLICATIONS_LISTED_HERE',
    /**
     * @value All users can access the space and ask to join. Only members can view and create publications. Space administrators process membership requests.
     * @description The term "Space" must not be translated, it is a male name.
     */
    PRIVACY_DESCRIPTION_ONLY_DESCRIPTION = 'SPACES.VISIBILITY_PRIVATE_DESCRIPTION',
    /**
     * PRIVATE DESCRIPTION ON LANDING PAGE (FOR END USERS)
     * @value All users can access the community and ask to join.<br>Only members can view and create publications.<br>Community administrators process membership requests.
     * @description The message users get when they end up on a private space they don't have access to (they can request to join).
     */
    PRIVACY_DESCRIPTION_ONLY_DESCRIPTION_SPACED = 'SPACES.PRIVATE_SPACE_LANDING_DESCRIPTION',
    /**
     * @value All users can access the space, view its publications and contribute.
     * @description The term "Space" must not be translated, it is a male name.
     *
     */
    PRIVACY_OPEN_DESCRIPTION = 'SPACES.OPEN_VISIBILITY_DESCRIPTION',
    /**
     * @value All users can search for the space, view its publications and ask to join it. Only members can contribute. Space administrators process membership requests.
     * @description The term "Space" must not be translated, it is a male name.
     */
    PRIVACY_READ_ONLY_DESCRIPTION = 'SPACES.VISIBILITY_MEMBERS_ONLY_CONTRIBUTION_DESCRIPTION',
    /**
     * @value Only members can see, access and contribute in the space. Members must be added by space administrators.
     * @description The term "Space" must not be translated, it is a male name.
     */
    PRIVACY_RESTRICTED_DESCRIPTION = 'SPACES.VISIBILITY_SECRET_DESCRIPTION',
    /**
     * @value Space saved successfully
     * @description Displayed on a notification toast to inform the used that the space have been created/saved
     * The term "Space" must not be translated, it is a male name.
     *
     */
    SAVED = 'SPACES.SAVED_NOTIFICATION',
    /**
     * @value An error occurred while saving space
     * @description Error notification displayed when an error occurs while the user tries to save/create a space
     * The term "Space" must not be translated, it is a male name.
     *
     */
    SETUP_SAVE_ERROR = 'SPACES.SAVED_ERROR_NOTIFICATION',
}

export const SPACES = { ...COMMUNITIES, ...SPACES_SPECIFIC_KEYS };

export const getCommunityPrivacy = (communityPrivacy: CommunityPrivacy) =>
    `COMMUNITY.PRIVACY_${snakeCase(communityPrivacy).toUpperCase()}`;

export enum CONTRIBUTION_FIELD {
    /**
     * @value Article
     * @description Button label on Contribution Field component
     */
    BUTTON_ARTICLE = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.BUTTON.ARTICLE',
    /**
     * @value Event
     * @description Label of the event creation button in the contribution field
     */
    BUTTON_EVENT = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.BUTTON.EVENT',
    /**
     * @value File
     */
    BUTTON_FILE = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.BUTTON.FILE',
    /**
     * @value Image
     */
    BUTTON_IMAGE = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.BUTTON.IMAGE',
    /**
     * @value Link
     */
    BUTTON_LINK = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.BUTTON.LINK',
    /**
     * @value Video
     * @description In the contribution field, the label of the button to insert a video
     */
    BUTTON_VIDEO = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.BUTTON.VIDEO',
    /**
     * @value Insert a media via a post
     */
    INSERT_MEDIA = 'CONTRIBUTION_FIELD.INSERT_MEDIA',
    /**
     * @value Share a file via a post
     * @description Tooltip displayed on the space contribution field on hovering the file button
     */
    SPACE_TOOLTIP_FILE = 'CONTRIBUTION_FIELD.SPACE_TOOLTIP_FILE',
    /**
     * @value Share an image via a post
     * @description Tooltip displayed on the space contribution field on hovering the image button
     */
    SPACE_TOOLTIP_IMAGE = 'CONTRIBUTION_FIELD.SPACE_TOOLTIP_IMAGE',
    /**
     * @value Share a video via a post
     * @description Tooltip displayed on the space contribution field on hovering the video button
     */
    SPACE_TOOLTIP_VIDEO = 'CONTRIBUTION_FIELD.SPACE_TOOLTIP_VIDEO',
    /**
     * @value Write an article
     */
    TOOLTIP_ARTICLE = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.TOOLTIP.WRITE.ARTICLE',
    /**
     * @value Schedule an event
     * @description In community pages there is a Floating button to create a new post. This is the label of one of the Floating Fab button that leads to the creation of an "event" post.
     * (this fab button with a calendar icon appears when the user hovers over the main fab button on the bottom right of the screen)
     */
    TOOLTIP_EVENT = 'FRONT.COMMUNITY.POST.CREATE.TYPE.EVENT',
    /**
     * @value Share a file
     */
    TOOLTIP_FILE = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.TOOLTIP.FILE',
    /**
     * @value Share an image
     */
    TOOLTIP_IMAGE = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.TOOLTIP.IMAGE',
    /**
     * @value Share a link
     */
    TOOLTIP_LINK = 'FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.TOOLTIP.LINK',
    /**
     * @value Write something
     * @description A tooltip displayed on the Post contribution field button
     */
    TOOLTIP_POST = 'COMMUNITY.POST_CONTRIBUTION_FIELD_TOOLTIP_POST',
    /**
     * @value Share a video
     */
    TOOLTIP_VIDEO = 'COMMUNITY.POST_CONTRIBUTION_FIELD_TOOLTIP_VIDEO',
}

export const getDescriptionForPostType = (type: string) =>
    `FRONT.COMMUNITY.POST.CONTRIBUTION_FIELD.PLACEHOLDER.${type}`;
